import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { proxyIfCdn } from '@/hooks/useAuthenticatedMedia';
import {
  Search,
  RefreshCw,
  Link as LinkIcon,
  Unlink,
  Edit,
  Eye,
  MapPin,
  Globe,
  Store,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Sparkles,
  ArrowRight,
  Plus,
  Wallet,
  Navigation,
  Settings,
  Users,
  Building2,
  ArrowRightLeft,
} from 'lucide-react';

interface Restaurant {
  id: string;
  name: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  website: string | null;
  description: string | null;
  latitude: string;
  longitude: string;
  category: string;
  brandName: string | null;
  brandCategoryName: string | null;
  kudozExternalId: number | null;
  externalRestaurantId: number | null;
  orderingEnabled: boolean;
  isActive: boolean | null;
  tempClosed: boolean | null;
  hoursJson: any;
  logoImageUrl: string | null;
  primaryImageUrl: string | null;
  kudozLastSync: string | null;
  overallRating: string | null;
  reviewCount: number | null;
  eatzyEmail: string | null;
  kudozWalletOwnerId: string | null;
  isCsrOnly: boolean | null;
  createdAt?: string;
}

interface UserSearchResult {
  id: string;
  email: string;
  name: string | null;
  displayName: string | null;
}

interface KudozRestaurant {
  id: number;
  name: string;
  brand_name: string;
  brand_category_name: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  website?: string;
  description: string;
  lat: number;
  long: number;
  is_featured: boolean;
  is_promo: boolean;
  is_active?: string;
  temp_closed: boolean;
  hours: string;
  isMapped: boolean;
  localRestaurantId?: string;
}

interface StagingEntry {
  id: string;
  kudozExternalId: number;
  name: string;
  city: string;
  state: string;
  zipCode: string;
  phone: string;
  website: string | null;
  brandName: string | null;
  brandCategoryName: string | null;
  description: string | null;
  isActive: boolean;
  logoImageUrl: string | null;
  status: string;
  candidateRestaurantIds: string[] | null;
  matchConfidence: number | null;
  processedAt: string | null;
  createdAt: string;
}

interface StagingResponse {
  entries: StagingEntry[];
  stats: {
    pending: number;
    mapped: number;
    created: number;
    dismissed: number;
  };
  total: number;
  page: number;
  limit: number;
}

interface RestaurantsResponse {
  restaurants: Restaurant[];
  stats: {
    total: number;
    mapped: number;
    unmapped: number;
    orderingEnabled: number;
    active: number;
    inactive: number;
  };
}

interface KudozRestaurantsResponse {
  restaurants: KudozRestaurant[];
  stats: {
    total: number;
    mapped: number;
    unmapped: number;
  };
  warning?: string;
}

interface SuggestedMatch {
  kudoz: KudozRestaurant;
  reviews: Restaurant;
  score: number;
  matchType: 'exact' | 'partial' | 'city';
}

function calculateMatchScore(kudoz: KudozRestaurant, reviews: Restaurant): { score: number; matchType: 'exact' | 'partial' | 'city' } {
  const kudozName = kudoz.name?.toLowerCase().trim() || '';
  const reviewsName = reviews.name?.toLowerCase().trim() || '';
  const kudozCity = kudoz.city?.toLowerCase().trim() || '';
  const reviewsCity = reviews.city?.toLowerCase().trim() || '';

  if (kudozCity !== reviewsCity) {
    return { score: 0, matchType: 'city' };
  }

  if (kudozName === reviewsName) {
    return { score: 100, matchType: 'exact' };
  }

  if (kudozName.includes(reviewsName) || reviewsName.includes(kudozName)) {
    return { score: 80, matchType: 'partial' };
  }

  const kudozWords = new Set(kudozName.split(/\s+/).filter(w => w.length > 2));
  const reviewsWords = new Set(reviewsName.split(/\s+/).filter(w => w.length > 2));
  let matches = 0;
  kudozWords.forEach(word => {
    if (reviewsWords.has(word)) matches++;
  });

  const totalWords = Math.max(kudozWords.size, reviewsWords.size);
  if (totalWords > 0 && matches > 0) {
    const wordScore = (matches / totalWords) * 70;
    if (wordScore >= 30) {
      return { score: wordScore, matchType: 'partial' };
    }
  }

  return { score: 0, matchType: 'city' };
}

export default function AdminRestaurants() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [activeTab, setActiveTab] = useState('partners');
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [eatzyBrowserSearch, setEatzyBrowserSearch] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);
  const [selectedKudozRestaurant, setSelectedKudozRestaurant] = useState<KudozRestaurant | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [compareDialogOpen, setCompareDialogOpen] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [reverseMapDialogOpen, setReverseMapDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Restaurant>>({});
  const [kudozSearch, setKudozSearch] = useState('');
  const [reviewsSearch, setReviewsSearch] = useState('');
  const [previewMapping, setPreviewMapping] = useState<{ reviews: Restaurant; kudoz: KudozRestaurant } | null>(null);
  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [walletEmail, setWalletEmail] = useState('');
  const [walletUserResults, setWalletUserResults] = useState<UserSearchResult[]>([]);
  const [walletUserSearching, setWalletUserSearching] = useState(false);
  const [selectedWalletUser, setSelectedWalletUser] = useState<UserSearchResult | null>(null);
  const [stagingPage, setStagingPage] = useState(1);
  const [stagingStatus, setStagingStatus] = useState<'pending' | 'created' | 'dismissed'>('pending');
  const [stagingSearch, setStagingSearch] = useState('');
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const buildQueryUrl = () => {
    const params = new URLSearchParams();
    if (filter && filter !== 'all') params.set('filter', filter);
    if (searchTerm) params.set('search', searchTerm);
    const query = params.toString();
    return query ? `/api/admin/restaurants?${query}` : '/api/admin/restaurants';
  };

  const getAuthHeaders = () => {
    const headers: Record<string, string> = {};
    const token = localStorage.getItem('auth_token');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  };

  const { data: localData, isLoading: localLoading } = useQuery<RestaurantsResponse>({
    queryKey: ['/api/admin/restaurants', { filter, searchTerm }],
    queryFn: async () => {
      const res = await fetch(buildQueryUrl(), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch restaurants');
      return res.json();
    },
  });

  const { data: kudozData, isLoading: kudozLoading } = useQuery<KudozRestaurantsResponse>({
    queryKey: ['/api/admin/kudoz-restaurants'],
    queryFn: async () => {
      const res = await fetch('/api/admin/kudoz-restaurants', {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch kudoz restaurants');
      return res.json();
    },
  });

  useEffect(() => {
    setStagingPage(1);
  }, [searchTerm, stagingStatus, filter, stagingSearch]);

  const buildStagingUrl = () => {
    const params = new URLSearchParams();
    params.set('status', stagingStatus);
    params.set('page', String(stagingPage));
    params.set('limit', '50');
    if (filter === 'active' || filter === 'inactive') params.set('activeFilter', filter);
    if (stagingSearch.trim()) params.set('search', stagingSearch.trim());
    return `/api/admin/staging?${params.toString()}`;
  };

  const { data: stagingData, isLoading: stagingLoading } = useQuery<StagingResponse>({
    queryKey: ['/api/admin/staging', { stagingPage, stagingStatus, filter, stagingSearch: stagingSearch.trim() }],
    queryFn: async () => {
      const res = await fetch(buildStagingUrl(), {
        credentials: 'include',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('Failed to fetch staging data');
      return res.json();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Restaurant> }) => {
      const res = await apiRequest('PATCH', `/api/admin/restaurants/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Restaurant updated successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      setEditDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ title: 'Error updating restaurant', description: error.message, variant: 'destructive' });
    },
  });

  const mapMutation = useMutation({
    mutationFn: async ({ id, kudozExternalId }: { id: string; kudozExternalId: number }) => {
      const res = await apiRequest('POST', `/api/admin/restaurants/${id}/map`, { kudozExternalId });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Restaurant mapped successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-restaurants'] });
      setMapDialogOpen(false);
      setKudozSearch('');
    },
    onError: (error: any) => {
      toast({ title: 'Error mapping restaurant', description: error.message, variant: 'destructive' });
    },
  });

  const unmapMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('POST', `/api/admin/restaurants/${id}/unmap`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Restaurant unmapped successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-restaurants'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error unmapping restaurant', description: error.message, variant: 'destructive' });
    },
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/admin/sync/kudoz/restaurants');
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({
        title: 'Sync completed',
        description: `Created: ${data.result?.created || 0}, Updated: ${data.result?.updated || 0}`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-restaurants'] });
    },
    onError: (error: any) => {
      toast({ title: 'Sync failed', description: error.message, variant: 'destructive' });
    },
  });

  const stagingMapMutation = useMutation({
    mutationFn: async ({ stagingId, restaurantId }: { stagingId: string; restaurantId: string }) => {
      const res = await apiRequest('POST', `/api/admin/staging/${stagingId}/map`, { restaurantId });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Restaurant mapped from staging' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/staging'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/kudoz-restaurants'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error mapping from staging', description: error.message, variant: 'destructive' });
    },
  });

  const stagingCreateMutation = useMutation({
    mutationFn: async (stagingId: string) => {
      const res = await apiRequest('POST', `/api/admin/staging/${stagingId}/create`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'New restaurant created from staging' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/staging'] });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error creating restaurant', description: error.message, variant: 'destructive' });
    },
  });

  const stagingDismissMutation = useMutation({
    mutationFn: async ({ stagingId, reason }: { stagingId: string; reason?: string }) => {
      const res = await apiRequest('POST', `/api/admin/staging/${stagingId}/dismiss`, { reason });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Staging entry dismissed' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/staging'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error dismissing entry', description: error.message, variant: 'destructive' });
    },
  });

  const linkWalletMutation = useMutation({
    mutationFn: async ({ id, userId }: { id: string; userId: string }) => {
      const res = await apiRequest('POST', `/api/admin/restaurants/${id}/wallet-owner`, { userId });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Wallet owner linked successfully' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      setWalletDialogOpen(false);
      setSelectedWalletUser(null);
      setWalletUserResults([]);
      setWalletEmail('');
    },
    onError: (error: any) => {
      toast({ title: 'Error linking wallet owner', description: error.message, variant: 'destructive' });
    },
  });

  const unlinkWalletMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('DELETE', `/api/admin/restaurants/${id}/wallet-owner`);
      return res.json();
    },
    onSuccess: () => {
      toast({ title: 'Wallet owner unlinked' });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
    },
    onError: (error: any) => {
      toast({ title: 'Error unlinking wallet owner', description: error.message, variant: 'destructive' });
    },
  });


  const resolveLocationMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('POST', `/api/admin/restaurants/${id}/resolve-location`);
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: 'Location resolved', description: `Updated to "${data.name}"` });
      queryClient.invalidateQueries({ queryKey: ['/api/admin/restaurants'] });
      setResolvingId(null);
    },
    onError: (error: any) => {
      toast({ title: 'Could not resolve location', description: error.message, variant: 'destructive' });
      setResolvingId(null);
    },
  });

  useEffect(() => {
    if (walletEmail.length < 3) {
      setWalletUserResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setWalletUserSearching(true);
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`/api/admin/users/search?email=${encodeURIComponent(walletEmail)}`, {
          headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        });
        if (res.ok) {
          const data = await res.json();
          setWalletUserResults(data);
        }
      } catch {
        setWalletUserResults([]);
      } finally {
        setWalletUserSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [walletEmail]);

  const handleWalletClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setWalletEmail(restaurant.eatzyEmail ?? '');
    setSelectedWalletUser(null);
    setWalletUserResults([]);
    setWalletDialogOpen(true);
  };

  const handleEdit = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setEditForm({
      name: restaurant.name,
      city: restaurant.city,
      state: restaurant.state,
      zipCode: restaurant.zipCode,
      phone: restaurant.phone,
      website: restaurant.website,
      description: restaurant.description,
      category: restaurant.category,
    });
    setEditDialogOpen(true);
  };

  const handleCompare = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    const kudozMatch = kudozData?.restaurants.find(k => k.id === restaurant.kudozExternalId);
    setSelectedKudozRestaurant(kudozMatch || null);
    setCompareDialogOpen(true);
  };

  const handleMapClick = (restaurant: Restaurant) => {
    setSelectedRestaurant(restaurant);
    setMapDialogOpen(true);
  };

  const handleReverseMapClick = (kudoz: KudozRestaurant) => {
    setSelectedKudozRestaurant(kudoz);
    setReverseMapDialogOpen(true);
  };

  const handlePreviewMapping = (reviews: Restaurant, kudoz: KudozRestaurant) => {
    setPreviewMapping({ reviews, kudoz });
  };

  const confirmMapping = () => {
    if (previewMapping) {
      mapMutation.mutate({
        id: previewMapping.reviews.id,
        kudozExternalId: previewMapping.kudoz.id,
      });
      setPreviewMapping(null);
      setMapDialogOpen(false);
      setReverseMapDialogOpen(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setFilter('all');
    setSearchTerm('');
  };

  const allLocalRestaurants = localData?.restaurants || [];
  const pendingCount = stagingData?.stats?.pending || 0;

  const partnerRestaurants = allLocalRestaurants
    .filter(r => !!r.kudozExternalId)
    .filter(r => {
      if (filter === 'ordering') return r.orderingEnabled;
      if (filter === 'active') return r.isActive !== false;
      if (filter === 'inactive') return r.isActive === false;
      return true;
    });

  const prospectRestaurants = allLocalRestaurants
    .filter(r => !r.kudozExternalId)
    .filter(r => {
      if (filter === 'active') return r.isActive !== false;
      if (filter === 'inactive') return r.isActive === false;
      if (filter === 'gps_placeholders') return r.name.startsWith('Restaurant near ');
      return true;
    })
    .sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : null;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : null;
      if (aTime !== null && bTime !== null) return bTime - aTime;
      if (aTime !== null) return -1;
      if (bTime !== null) return 1;
      return a.name.localeCompare(b.name);
    });

  const suggestedMatches: SuggestedMatch[] = (() => {
    if (!localData?.restaurants || !kudozData?.restaurants) return [];
    const unmappedReviews = localData.restaurants.filter(r => !r.kudozExternalId);
    const unmappedKudoz = kudozData.restaurants.filter(k => !k.isMapped);
    const matches: SuggestedMatch[] = [];
    for (const kudoz of unmappedKudoz) {
      for (const reviews of unmappedReviews) {
        const { score, matchType } = calculateMatchScore(kudoz, reviews);
        if (score >= 30) {
          matches.push({ kudoz, reviews, score, matchType });
        }
      }
    }
    matches.sort((a, b) => b.score - a.score);
    return matches.slice(0, 20);
  })();

  const eatzyBrowserFiltered = (kudozData?.restaurants || []).filter(r => {
    if (!eatzyBrowserSearch) return true;
    const s = eatzyBrowserSearch.toLowerCase();
    return r.name?.toLowerCase().includes(s) ||
      r.city?.toLowerCase().includes(s) ||
      r.brand_name?.toLowerCase().includes(s) ||
      String(r.id).includes(s);
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* ── Page header ── */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">Restaurant Management</h1>
            {pendingCount > 0 && (
              <Badge
                className="bg-orange-500 text-white cursor-pointer"
                onClick={() => setActiveTab('mapping')}
              >
                {pendingCount} pending sync
              </Badge>
            )}
          </div>
          <Button
            variant="outline"
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            {syncMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Sync from Eatzy
          </Button>
        </div>

        {/* ── Stats row ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{localData?.stats.mapped || 0}</div>
              <p className="text-sm text-muted-foreground">Active Partners</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{localData?.stats.unmapped || 0}</div>
              <p className="text-sm text-muted-foreground">Prospects</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-orange-600">{pendingCount}</div>
              <p className="text-sm text-muted-foreground">Pending Sync</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold">{kudozData?.stats.total || 0}</div>
              <p className="text-sm text-muted-foreground">Total Eatzy</p>
            </CardContent>
          </Card>
        </div>

        {/* ── Tabs ── */}
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <div className="flex flex-wrap items-center gap-3">
            <TabsList>
              <TabsTrigger value="partners" className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" />
                Partners
              </TabsTrigger>
              <TabsTrigger value="prospects" className="flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                Prospects
                {(localData?.stats.unmapped || 0) > 0 && (
                  <Badge className="ml-1 bg-blue-500 text-white text-xs px-1.5 py-0.5">
                    {localData?.stats.unmapped}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="mapping" className="flex items-center gap-1.5">
                <ArrowRightLeft className="w-3.5 h-3.5" />
                Mapping
                {pendingCount > 0 && (
                  <Badge className="ml-1 bg-orange-500 text-white text-xs px-1.5 py-0.5">
                    {pendingCount}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Search + filter — shown for Partners and Prospects tabs */}
            {activeTab !== 'mapping' && (
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="relative flex-1 min-w-48">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search restaurants…"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-44">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {activeTab === 'partners' && (
                      <SelectItem value="ordering">Ordering Enabled</SelectItem>
                    )}
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    {activeTab === 'prospects' && (
                      <SelectItem value="gps_placeholders">GPS Placeholders</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* ── TAB 1: Partners ── */}
          <TabsContent value="partners" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="w-4 h-4" />
                  Active Partners
                  <span className="text-muted-foreground font-normal text-sm ml-1">({partnerRestaurants.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {localLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                ) : partnerRestaurants.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No partners found{searchTerm ? ` matching "${searchTerm}"` : ''}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">Location</th>
                          <th className="text-left py-3 px-2">Eatzy ID</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Wallet</th>
                          <th className="text-right py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partnerRestaurants.map((restaurant) => (
                          <tr key={restaurant.id} className="border-b hover-elevate">
                            <td className="py-3 px-2">
                              <div className="font-medium">{restaurant.name}</div>
                              {restaurant.brandName && (
                                <div className="text-xs text-muted-foreground">{restaurant.brandName}</div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                {restaurant.city}, {restaurant.state}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">
                                #{restaurant.kudozExternalId}
                              </span>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex flex-col gap-1">
                                {restaurant.orderingEnabled && (
                                  <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Ordering</Badge>
                                )}
                                {restaurant.isActive !== false ? (
                                  <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
                                ) : (
                                  <Badge className="text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Inactive</Badge>
                                )}
                                {restaurant.isCsrOnly && (
                                  <Badge className="text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">CSR Only</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              {restaurant.kudozWalletOwnerId ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  <span className="text-xs truncate max-w-[120px]" title={restaurant.eatzyEmail ?? undefined}>
                                    {restaurant.eatzyEmail ?? 'Linked'}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  <span className="text-xs">No wallet</span>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex justify-end items-center gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => navigate(`/admin/restaurants/${restaurant.kudozExternalId}/manage`)}
                                  title="Manage restaurant settings"
                                >
                                  <Settings className="w-3.5 h-3.5 mr-1" />
                                  Manage
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleCompare(restaurant)}
                                  title="Compare with Eatzy"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => unmapMutation.mutate(restaurant.id)}
                                  title="Unmap from Eatzy"
                                >
                                  <Unlink className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleWalletClick(restaurant)}
                                  title="Link wallet owner"
                                >
                                  <Wallet className="w-4 h-4" />
                                </Button>
                                {restaurant.kudozWalletOwnerId && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => unlinkWalletMutation.mutate(restaurant.id)}
                                    title="Unlink wallet owner"
                                    disabled={unlinkWalletMutation.isPending}
                                  >
                                    {unlinkWalletMutation.isPending ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <XCircle className="w-4 h-4" />
                                    )}
                                  </Button>
                                )}
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleEdit(restaurant)}
                                  title="Edit metadata"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB 2: Prospects ── */}
          <TabsContent value="prospects" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="w-4 h-4" />
                  Prospects — Tier 1 Locations
                  <span className="text-muted-foreground font-normal text-sm ml-1">({prospectRestaurants.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {localLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                ) : prospectRestaurants.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground text-sm">
                    No prospects found{searchTerm ? ` matching "${searchTerm}"` : ''}.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">Location</th>
                          <th className="text-left py-3 px-2">Category</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-right py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {prospectRestaurants.map((restaurant) => {
                          const isGpsPlaceholder = restaurant.name.startsWith('Restaurant near ');
                          return (
                            <tr
                              key={restaurant.id}
                              className={`border-b hover-elevate ${isGpsPlaceholder ? 'bg-yellow-50/60 dark:bg-yellow-950/20' : ''}`}
                            >
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-2">
                                  {isGpsPlaceholder && (
                                    <span title="GPS placeholder — name not resolved"><Navigation className="w-3.5 h-3.5 text-yellow-600 flex-shrink-0" /></span>
                                  )}
                                  <div>
                                    <div className={`font-medium ${isGpsPlaceholder ? 'text-yellow-800 dark:text-yellow-300' : ''}`}>
                                      {restaurant.name}
                                    </div>
                                    {restaurant.brandName && (
                                      <div className="text-xs text-muted-foreground">{restaurant.brandName}</div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  {restaurant.city}, {restaurant.state}
                                </div>
                              </td>
                              <td className="py-3 px-2">
                                <Badge variant="secondary" className="text-xs">
                                  {restaurant.brandCategoryName || restaurant.category || 'N/A'}
                                </Badge>
                              </td>
                              <td className="py-3 px-2">
                                {restaurant.isActive !== false ? (
                                  <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>
                                ) : (
                                  <Badge className="text-xs bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">Inactive</Badge>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex justify-end gap-1">
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleMapClick(restaurant)}
                                    title="Map to Eatzy"
                                  >
                                    <LinkIcon className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleEdit(restaurant)}
                                    title="Edit metadata"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  {isGpsPlaceholder && (
                                    <Button
                                      size="icon"
                                      variant="ghost"
                                      title="Resolve restaurant name from GPS"
                                      disabled={resolvingId === restaurant.id}
                                      onClick={() => {
                                        setResolvingId(restaurant.id);
                                        resolveLocationMutation.mutate(restaurant.id);
                                      }}
                                    >
                                      {resolvingId === restaurant.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                      ) : (
                                        <Navigation className="w-4 h-4 text-blue-500" />
                                      )}
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── TAB 3: Mapping ── */}
          <TabsContent value="mapping" className="mt-4 space-y-6">

            {/* Section 1: Incoming queue */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Store className="w-4 h-4 text-orange-600" />
                    Incoming Eatzy Queue
                    {pendingCount > 0 && (
                      <Badge className="bg-orange-500 text-white">{pendingCount} pending</Badge>
                    )}
                  </CardTitle>
                  <div className="flex gap-3 text-sm flex-wrap">
                    <button
                      className={`font-medium ${stagingStatus === 'pending' ? 'text-orange-600 underline' : 'text-muted-foreground'}`}
                      onClick={() => setStagingStatus('pending')}
                    >
                      Pending: {stagingData?.stats?.pending || 0}
                    </button>
                    <span className="text-muted-foreground">Mapped: {stagingData?.stats?.mapped || 0}</span>
                    <button
                      className={`font-medium ${stagingStatus === 'created' ? 'text-blue-600 underline' : 'text-muted-foreground'}`}
                      onClick={() => setStagingStatus('created')}
                    >
                      Created: {stagingData?.stats?.created || 0}
                    </button>
                    <button
                      className={`font-medium ${stagingStatus === 'dismissed' ? 'text-muted-foreground underline' : 'text-muted-foreground'}`}
                      onClick={() => setStagingStatus('dismissed')}
                    >
                      Dismissed: {stagingData?.stats?.dismissed || 0}
                    </button>
                  </div>
                </div>
                <div className="relative mt-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, city, brand or category…"
                    value={stagingSearch}
                    onChange={(e) => setStagingSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                {stagingLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    {stagingData && (stagingData.total ?? 0) > 0 && (
                      <div className="text-sm text-muted-foreground">
                        Showing {((stagingPage - 1) * (stagingData.limit ?? 50)) + 1}–{Math.min(stagingPage * (stagingData.limit ?? 50), stagingData.total ?? 0)} of {stagingData.total ?? 0} restaurants
                      </div>
                    )}
                    {!stagingData?.entries?.length ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No entries in this category.
                      </div>
                    ) : (
                      stagingData.entries.map((entry) => (
                        <div key={entry.id} className="border rounded-lg p-4 space-y-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-3">
                              {entry.logoImageUrl && (
                                <img
                                  src={proxyIfCdn(entry.logoImageUrl) ?? undefined}
                                  alt={entry.name}
                                  className="w-12 h-12 rounded object-cover"
                                  loading="lazy"
                                />
                              )}
                              <div>
                                <div className="font-medium">{entry.name}</div>
                                {entry.brandName && (
                                  <div className="text-xs text-muted-foreground">{entry.brandName}</div>
                                )}
                                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                  <MapPin className="w-3 h-3" />
                                  {entry.city}, {entry.state} {entry.zipCode}
                                </div>
                                {entry.brandCategoryName && (
                                  <Badge variant="secondary" className="text-xs mt-1">
                                    {entry.brandCategoryName}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              <Badge className={entry.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {entry.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                              {entry.matchConfidence && entry.matchConfidence >= 40 && (
                                <Badge variant="outline" className="text-xs">
                                  {entry.matchConfidence >= 85 ? 'High' : entry.matchConfidence >= 70 ? 'Medium' : 'Possible'} match
                                </Badge>
                              )}
                            </div>
                          </div>

                          {entry.candidateRestaurantIds && entry.candidateRestaurantIds.length > 0 && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-md p-3 space-y-2">
                              <div className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                                Potential Matches Found ({entry.candidateRestaurantIds.length})
                              </div>
                              <div className="space-y-1">
                                {entry.candidateRestaurantIds.slice(0, 3).map((candidateId) => {
                                  const candidate = localData?.restaurants.find(r => r.id === candidateId);
                                  return candidate ? (
                                    <div key={candidateId} className="flex items-center justify-between p-2 bg-white dark:bg-gray-800 rounded border">
                                      <div className="text-sm">
                                        <span className="font-medium">{candidate.name}</span>
                                        <span className="text-muted-foreground ml-2">
                                          {candidate.city}, {candidate.state}
                                        </span>
                                      </div>
                                      <Button
                                        size="sm"
                                        onClick={() => stagingMapMutation.mutate({ stagingId: entry.id, restaurantId: candidateId })}
                                        disabled={stagingMapMutation.isPending}
                                      >
                                        <LinkIcon className="w-3 h-3 mr-1" />
                                        Map to this
                                      </Button>
                                    </div>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}

                          <div className="flex gap-2 pt-2 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => stagingCreateMutation.mutate(entry.id)}
                              disabled={stagingCreateMutation.isPending}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Create New Restaurant
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-muted-foreground"
                              onClick={() => stagingDismissMutation.mutate({ stagingId: entry.id })}
                              disabled={stagingDismissMutation.isPending}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Dismiss
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                    {(stagingData?.total ?? 0) > (stagingData?.limit ?? 50) && (
                      <div className="flex items-center justify-between pt-4 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStagingPage(p => Math.max(1, p - 1))}
                          disabled={stagingPage <= 1}
                        >
                          Previous
                        </Button>
                        <span className="text-sm text-muted-foreground">
                          Page {stagingPage} of {Math.ceil((stagingData?.total ?? 0) / (stagingData?.limit ?? 50))}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setStagingPage(p => p + 1)}
                          disabled={stagingPage >= Math.ceil((stagingData?.total ?? 0) / (stagingData?.limit ?? 50))}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Section 2: Suggested matches */}
            {suggestedMatches.length > 0 && (
              <Card className="border-purple-200 dark:border-purple-900 bg-purple-50/50 dark:bg-purple-950/20">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-purple-600" />
                    Suggested Matches ({suggestedMatches.length})
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    These Eatzy restaurants appear to match existing Prospect locations. Click to review and confirm.
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {suggestedMatches.map((match) => (
                      <div
                        key={`${match.kudoz.id}-${match.reviews.id}`}
                        className="flex items-center justify-between p-3 rounded-md border bg-background hover-elevate cursor-pointer"
                        onClick={() => handlePreviewMapping(match.reviews, match.kudoz)}
                      >
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{match.kudoz.name}</div>
                            <div className="text-xs text-muted-foreground">Eatzy · {match.kudoz.city}, {match.kudoz.state}</div>
                          </div>
                          <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{match.reviews.name}</div>
                            <div className="text-xs text-muted-foreground">Prospect · {match.reviews.city}, {match.reviews.state}</div>
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className={`ml-2 flex-shrink-0 ${match.matchType === 'exact' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'}`}
                        >
                          {match.matchType === 'exact' ? 'Exact' : `${Math.round(match.score)}%`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Section 3: Eatzy browser */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Eatzy Restaurant Browser
                  <span className="text-muted-foreground font-normal text-sm ml-1">
                    ({kudozData?.stats.total || 0} total · {kudozData?.stats.mapped || 0} mapped · {kudozData?.stats.unmapped || 0} unmapped)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {kudozData?.warning && (
                  <div className="mb-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-800 p-3 text-sm text-yellow-800 dark:text-yellow-300">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>Eatzy backend API unavailable — showing locally synced data. {kudozData.warning}</span>
                  </div>
                )}
                <div className="relative mb-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, city, or Eatzy ID…"
                    value={eatzyBrowserSearch}
                    onChange={(e) => setEatzyBrowserSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>
                {kudozLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin" />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">ID</th>
                          <th className="text-left py-3 px-2">Name</th>
                          <th className="text-left py-3 px-2">Location</th>
                          <th className="text-left py-3 px-2">Category</th>
                          <th className="text-left py-3 px-2">Flags</th>
                          <th className="text-left py-3 px-2">Mapping</th>
                          <th className="text-right py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {eatzyBrowserFiltered.map((restaurant) => (
                          <tr key={restaurant.id} className="border-b hover-elevate">
                            <td className="py-3 px-2 font-mono text-xs">{restaurant.id}</td>
                            <td className="py-3 px-2">
                              <div className="font-medium">{restaurant.name}</div>
                              {restaurant.brand_name && (
                                <div className="text-xs text-muted-foreground">{restaurant.brand_name}</div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="w-3 h-3" />
                                {restaurant.city}, {restaurant.state}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant="secondary" className="text-xs">
                                {restaurant.brand_category_name || 'N/A'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex flex-wrap gap-1">
                                {restaurant.is_featured && (
                                  <Badge className="text-xs bg-purple-100 text-purple-800">Featured</Badge>
                                )}
                                {restaurant.is_promo && (
                                  <Badge className="text-xs bg-yellow-100 text-yellow-800">Promo</Badge>
                                )}
                                {restaurant.temp_closed && (
                                  <Badge className="text-xs bg-red-100 text-red-800">Temp Closed</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              {restaurant.isMapped ? (
                                <div className="flex items-center gap-1 text-green-600">
                                  <CheckCircle className="w-4 h-4" />
                                  <span className="text-xs">Partner</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-muted-foreground">
                                  <XCircle className="w-4 h-4" />
                                  <span className="text-xs">Not mapped</span>
                                </div>
                              )}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex justify-end gap-1">
                                {!restaurant.isMapped && (
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={() => handleReverseMapClick(restaurant)}
                                    title="Map to a Prospect restaurant"
                                  >
                                    <LinkIcon className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {eatzyBrowserFiltered.length === 0 && (
                          <tr>
                            <td colSpan={7} className="py-8 text-center text-muted-foreground text-sm">
                              {eatzyBrowserSearch ? `No Eatzy restaurants found matching "${eatzyBrowserSearch}".` : 'No Eatzy restaurants available.'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Restaurant</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input 
                  value={editForm.name || ''} 
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input 
                  value={editForm.category || ''} 
                  onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>City</Label>
                <Input 
                  value={editForm.city || ''} 
                  onChange={(e) => setEditForm({...editForm, city: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>State</Label>
                <Input 
                  value={editForm.state || ''} 
                  onChange={(e) => setEditForm({...editForm, state: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Zip Code</Label>
                <Input 
                  value={editForm.zipCode || ''} 
                  onChange={(e) => setEditForm({...editForm, zipCode: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input 
                  value={editForm.phone || ''} 
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Website</Label>
                <Input 
                  value={editForm.website || ''} 
                  onChange={(e) => setEditForm({...editForm, website: e.target.value})}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={editForm.description || ''} 
                  onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={() => selectedRestaurant && updateMutation.mutate({ id: selectedRestaurant.id, data: editForm })}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={compareDialogOpen} onOpenChange={setCompareDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Compare Restaurant Data</DialogTitle>
            </DialogHeader>
            <div className="grid md:grid-cols-2 gap-6 py-4">
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Store className="w-4 h-4" />
                  Reviews Database
                </h3>
                <div className="space-y-2 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> {selectedRestaurant?.name}</div>
                  <div><span className="text-muted-foreground">Brand:</span> {selectedRestaurant?.brandName || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Category:</span> {selectedRestaurant?.brandCategoryName || selectedRestaurant?.category}</div>
                  <div><span className="text-muted-foreground">City:</span> {selectedRestaurant?.city}</div>
                  <div><span className="text-muted-foreground">State:</span> {selectedRestaurant?.state}</div>
                  <div><span className="text-muted-foreground">Phone:</span> {selectedRestaurant?.phone}</div>
                  <div><span className="text-muted-foreground">Website:</span> {selectedRestaurant?.website || 'N/A'}</div>
                  <div><span className="text-muted-foreground">Description:</span> {selectedRestaurant?.description?.slice(0, 100) || 'N/A'}...</div>
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Eatzy Backend
                </h3>
                {selectedKudozRestaurant ? (
                  <div className="space-y-2 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> {selectedKudozRestaurant.name}</div>
                    <div><span className="text-muted-foreground">Brand:</span> {selectedKudozRestaurant.brand_name || 'N/A'}</div>
                    <div><span className="text-muted-foreground">Category:</span> {selectedKudozRestaurant.brand_category_name}</div>
                    <div><span className="text-muted-foreground">City:</span> {selectedKudozRestaurant.city}</div>
                    <div><span className="text-muted-foreground">State:</span> {selectedKudozRestaurant.state}</div>
                    <div><span className="text-muted-foreground">Phone:</span> {selectedKudozRestaurant.phone}</div>
                    <div><span className="text-muted-foreground">Website:</span> {selectedKudozRestaurant.website || 'N/A'}</div>
                    <div><span className="text-muted-foreground">Description:</span> {selectedKudozRestaurant.description?.slice(0, 100) || 'N/A'}...</div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">No Eatzy data found</div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCompareDialogOpen(false)}>Close</Button>
              {selectedRestaurant && (
                <Button onClick={() => { setCompareDialogOpen(false); handleEdit(selectedRestaurant); }}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Reviews Data
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Map Restaurant to Eatzy</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Select an Eatzy restaurant to map to "<span className="font-medium text-foreground">{selectedRestaurant?.name}</span>":
              </p>
              <Input 
                placeholder="Search Eatzy restaurants..." 
                value={kudozSearch}
                onChange={(e) => setKudozSearch(e.target.value)}
                className="mb-3"
              />
              <div className="max-h-72 overflow-y-auto space-y-2">
                {kudozData?.restaurants
                  .filter(k => {
                    if (!kudozSearch) return true;
                    const search = kudozSearch.toLowerCase();
                    return k.name?.toLowerCase().includes(search) ||
                           k.city?.toLowerCase().includes(search) ||
                           k.brand_name?.toLowerCase().includes(search) ||
                           String(k.id).includes(search);
                  })
                  .map((kudoz) => (
                  <div 
                    key={kudoz.id}
                    className={`flex items-center justify-between p-3 border rounded-md hover-elevate cursor-pointer ${kudoz.isMapped ? 'bg-muted/50' : ''}`}
                    onClick={() => selectedRestaurant && mapMutation.mutate({ id: selectedRestaurant.id, kudozExternalId: kudoz.id })}
                  >
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {kudoz.name}
                        {kudoz.isMapped && (
                          <Badge variant="outline" className="text-xs">Already mapped</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {kudoz.city}, {kudoz.state} • ID: {kudoz.id}
                      </div>
                    </div>
                    <LinkIcon className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
                {kudozData?.restaurants.filter(k => {
                  if (!kudozSearch) return true;
                  const search = kudozSearch.toLowerCase();
                  return k.name?.toLowerCase().includes(search) ||
                         k.city?.toLowerCase().includes(search);
                }).length === 0 && (
                  <div className="text-center text-muted-foreground py-4">
                    No Eatzy restaurants found matching "{kudozSearch}"
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setMapDialogOpen(false); setKudozSearch(''); }}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={reverseMapDialogOpen} onOpenChange={setReverseMapDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Map Eatzy Restaurant to Reviews</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Select a Reviews restaurant to map "<span className="font-medium text-foreground">{selectedKudozRestaurant?.name}</span>" to:
              </p>
              <Input 
                placeholder="Search Reviews restaurants..." 
                value={reviewsSearch}
                onChange={(e) => setReviewsSearch(e.target.value)}
                className="mb-3"
              />
              <div className="max-h-72 overflow-y-auto space-y-2">
                {localData?.restaurants
                  .filter(r => {
                    if (!reviewsSearch) return true;
                    const search = reviewsSearch.toLowerCase();
                    return r.name?.toLowerCase().includes(search) ||
                           r.city?.toLowerCase().includes(search);
                  })
                  .map((reviews) => (
                  <div 
                    key={reviews.id}
                    className={`flex items-center justify-between p-3 border rounded-md hover-elevate cursor-pointer ${reviews.kudozExternalId ? 'bg-muted/50' : ''}`}
                    onClick={() => selectedKudozRestaurant && handlePreviewMapping(reviews, selectedKudozRestaurant)}
                  >
                    <div>
                      <div className="font-medium text-sm flex items-center gap-2">
                        {reviews.name}
                        {reviews.kudozExternalId && (
                          <Badge variant="outline" className="text-xs">Already mapped</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {reviews.city}, {reviews.state}
                      </div>
                    </div>
                    <LinkIcon className="w-4 h-4 text-muted-foreground" />
                  </div>
                ))}
                {localData?.restaurants.filter(r => {
                  if (!reviewsSearch) return true;
                  const search = reviewsSearch.toLowerCase();
                  return r.name?.toLowerCase().includes(search) ||
                         r.city?.toLowerCase().includes(search);
                }).length === 0 && (
                  <div className="text-center text-muted-foreground py-4">
                    No Reviews restaurants found matching "{reviewsSearch}"
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setReverseMapDialogOpen(false); setReviewsSearch(''); }}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={walletDialogOpen} onOpenChange={(open) => {
          setWalletDialogOpen(open);
          if (!open) { setSelectedWalletUser(null); setWalletUserResults([]); setWalletEmail(''); }
        }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Link Wallet Owner</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Associate a user's Kudoz wallet with <span className="font-medium text-foreground">{selectedRestaurant?.name}</span>. Search by the owner's email address.
              </p>
              <div className="space-y-1">
                <Label>Search by email</Label>
                <div className="relative">
                  <Input
                    placeholder="owner@example.com"
                    value={walletEmail}
                    onChange={(e) => {
                      setWalletEmail(e.target.value);
                      setSelectedWalletUser(null);
                    }}
                  />
                  {walletUserSearching && (
                    <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  )}
                </div>
                {walletEmail.length > 0 && walletEmail.length < 3 && (
                  <p className="text-xs text-muted-foreground">Enter at least 3 characters to search</p>
                )}
              </div>
              {walletUserResults.length > 0 && (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Matching users</Label>
                  <div className="max-h-48 overflow-y-auto space-y-1 border rounded-md p-1">
                    {walletUserResults.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setSelectedWalletUser(user)}
                        className={`flex flex-col p-2 rounded-md cursor-pointer hover-elevate ${selectedWalletUser?.id === user.id ? 'bg-accent' : ''}`}
                      >
                        <span className="text-sm font-medium">{user.displayName || user.name || 'Unnamed user'}</span>
                        <span className="text-xs text-muted-foreground">{user.email}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {walletEmail.length >= 3 && !walletUserSearching && walletUserResults.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-2">No users found matching "{walletEmail}"</p>
              )}
              {selectedWalletUser && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-muted/50 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                  <span>Will link wallet owned by <span className="font-medium">{selectedWalletUser.email}</span></span>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setWalletDialogOpen(false); setSelectedWalletUser(null); setWalletUserResults([]); setWalletEmail(''); }}>
                Cancel
              </Button>
              <Button
                onClick={() => selectedRestaurant && selectedWalletUser && linkWalletMutation.mutate({ id: selectedRestaurant.id, userId: selectedWalletUser.id })}
                disabled={!selectedWalletUser || linkWalletMutation.isPending}
              >
                {linkWalletMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Link Wallet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!previewMapping} onOpenChange={() => setPreviewMapping(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Confirm Restaurant Mapping</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Review the details below before confirming the mapping. This will link the Eatzy restaurant to the Reviews restaurant, enabling ordering capabilities.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                  <h3 className="font-semibold flex items-center gap-2 text-blue-600">
                    <Store className="w-4 h-4" />
                    Eatzy (Ordering Source)
                  </h3>
                  {previewMapping?.kudoz && (
                    <div className="space-y-2 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{previewMapping.kudoz.name}</span></div>
                      <div><span className="text-muted-foreground">Brand:</span> {previewMapping.kudoz.brand_name || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Category:</span> {previewMapping.kudoz.brand_category_name}</div>
                      <div><span className="text-muted-foreground">Location:</span> {previewMapping.kudoz.city}, {previewMapping.kudoz.state} {previewMapping.kudoz.zip}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {previewMapping.kudoz.phone}</div>
                      <div><span className="text-muted-foreground">ID:</span> <span className="font-mono text-xs">{previewMapping.kudoz.id}</span></div>
                    </div>
                  )}
                </div>
                <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                  <h3 className="font-semibold flex items-center gap-2 text-green-600">
                    <Globe className="w-4 h-4" />
                    Reviews Database
                  </h3>
                  {previewMapping?.reviews && (
                    <div className="space-y-2 text-sm">
                      <div><span className="text-muted-foreground">Name:</span> <span className="font-medium">{previewMapping.reviews.name}</span></div>
                      <div><span className="text-muted-foreground">Brand:</span> {previewMapping.reviews.brandName || 'N/A'}</div>
                      <div><span className="text-muted-foreground">Category:</span> {previewMapping.reviews.brandCategoryName || previewMapping.reviews.category}</div>
                      <div><span className="text-muted-foreground">Location:</span> {previewMapping.reviews.city}, {previewMapping.reviews.state} {previewMapping.reviews.zipCode}</div>
                      <div><span className="text-muted-foreground">Phone:</span> {previewMapping.reviews.phone}</div>
                      <div><span className="text-muted-foreground">Reviews:</span> {previewMapping.reviews.reviewCount || 0} reviews</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setPreviewMapping(null)}>Cancel</Button>
              <Button 
                onClick={confirmMapping}
                disabled={mapMutation.isPending}
              >
                {mapMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <LinkIcon className="w-4 h-4 mr-2" />
                Confirm Mapping
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

    </AdminLayout>
  );
}
